/**
 * 预算计算工具函数
 * 用于计算项目预算健康度和状态
 */

export type BudgetStatus = 'healthy' | 'good' | 'warning' | 'danger' | 'over' | 'unset';

export interface BudgetHealth {
  status: BudgetStatus;
  label: string;
  color: string;
  icon: string;
  percentage: number;
  remaining: number;
  message: string;
}

/**
 * 计算预算健康度
 * @param budget 预算金额
 * @param actualCost 实际成本
 * @returns 预算健康度信息
 */
export const calculateBudgetHealth = (budget: number, actualCost: number): BudgetHealth => {
  if (!budget || budget === 0) {
    return { 
      status: 'unset', 
      label: '未设置', 
      color: '#999', 
      icon: '⚪',
      percentage: 0, 
      remaining: 0,
      message: '请设置项目预算以启用成本监控'
    };
  }
  
  const percentage = (actualCost / budget) * 100;
  const remaining = budget - actualCost;
  
  if (percentage <= 50) {
    return { 
      status: 'healthy', 
      label: '健康', 
      color: '#52c41a', 
      icon: '✅',
      percentage, 
      remaining,
      message: '预算充足，项目成本控制良好'
    };
  } else if (percentage <= 70) {
    return { 
      status: 'good', 
      label: '良好', 
      color: '#1677ff', 
      icon: '🔵',
      percentage, 
      remaining,
      message: '预算使用正常，请继续保持'
    };
  } else if (percentage <= 90) {
    return { 
      status: 'warning', 
      label: '注意', 
      color: '#fa8c16', 
      icon: '⚠️',
      percentage, 
      remaining,
      message: '预算使用较多，请注意成本控制'
    };
  } else if (percentage <= 100) {
    return { 
      status: 'danger', 
      label: '紧张', 
      color: '#f5222d', 
      icon: '🔴',
      percentage, 
      remaining,
      message: '预算即将用尽，请严格控制支出'
    };
  } else {
    return { 
      status: 'over', 
      label: '超支', 
      color: '#cf1322', 
      icon: '❌',
      percentage, 
      remaining,
      message: `已超支 ¥${Math.abs(remaining).toLocaleString()}，请及时处理`
    };
  }
};

/**
 * 格式化金额显示
 * @param value 金额
 * @param showWan 是否显示万元单位（超过1万时）
 * @returns 格式化后的金额字符串
 */
export const formatMoney = (value: number, showWan: boolean = true): string => {
  if (showWan && value >= 10000) {
    return `¥${(value / 10000).toFixed(1)}万`;
  }
  return `¥${value.toLocaleString()}`;
};

/**
 * 获取预算状态的标签颜色（用于Ant Design Tag组件）
 */
export const getBudgetTagColor = (status: BudgetStatus): string => {
  const colorMap: Record<BudgetStatus, string> = {
    healthy: 'success',
    good: 'processing',
    warning: 'warning',
    danger: 'error',
    over: 'error',
    unset: 'default'
  };
  return colorMap[status];
};
